/**
 * Plan- och bygglagen (PBL) – kapitelvisa förklaringar.
 * OBS: Vi återger INTE fulltextlag – bara korta utdrag med källhänvisning
 * så att sidorna är originalinnehåll för SEO.
 */
export const pblRules = [
  {
    slug: "1-kap-syfte",
    title: "PBL 1 kap – Syfte och innehåll",
    h1: "PBL 1 kap – Syfte och innehåll",
    metaDescription:
      "Plan- och bygglagens 1 kapitel förklarar lagens syfte: att främja god samhällsutveckling och byggande. Läs sammanfattning + praktisk innebörd.",
    keywords: "PBL 1 kap, plan- och bygglagen syfte, PBL 2010:900",
    intro:
      "Kapitel 1 slår fast att PBL syftar till en hållbar samhällsutveckling där mark- och vattenanvändning ska främja goda sociala levnadsförhållanden och en god miljö.",
    verified_source: { sfs: "2010:900", chapter: "1", paragraph: "1" },
    excerpt: {
      text: "Bestämmelserna syftar till att, med hänsyn till den enskilda människans frihet, främja en samhällsutveckling med jämlika och goda sociala levnadsförhållanden och en god och långsiktigt hållbar livsmiljö.",
      source: "PBL 1 kap. 1 § (SFS 2010:900)",
    },
    examples: [
      { type: "Villa", title: "Byggnadens placering", text: "När du söker bygglov ska kommunen bedöma om placeringen är förenlig med god samhällsutveckling – inte bara detaljplanen." },
      { type: "Kommun", title: "Kommunens rådighet", text: "Kommunen ansvarar för planläggning – staten kan inte gå in och detaljstyra enskilda bygglov." },
    ],
    mistakes: [
      "Att tro att PBL bara handlar om bygglov – kapitel 1 sätter ramen för både planering, byggande och tillsyn.",
      "Att bortse från 'långsiktigt hållbar livsmiljö' – kommunen kan neka lov även om detaljplan tillåter, om miljöhänsyn brister.",
    ],
    municipality_variations:
      "Alla kommuner följer samma 1 kap men vissa kommuner tolkar 'god samhällsutveckling' strängare – t.ex. tätorter med kulturmiljöskydd.",
    faq: [
      { q: "Vad är PBL:s huvudsyfte?", a: "Att främja en hållbar samhällsutveckling där mark- och vattenanvändning bidrar till goda levnadsförhållanden och god miljö." },
      { q: "Gäller PBL i hela Sverige?", a: "Ja, PBL är rikstäckande, men tillämpningen sköts av respektive kommun via byggnadsnämnden." },
      { q: "Vad skiljer PBL från Miljöbalken?", a: "PBL styr planering och byggande, Miljöbalken styr miljöpåverkan. Ofta överlappar de – t.ex. vid strandskydd." },
    ],
    related: ["pbl/9-kap-bygglov-rivningslov-marklov", "pbl/5-kap-detaljplan", "detaljplan/vad-ar-en-detaljplan"],
    updated: "2026-07-04",
  },
  {
    slug: "9-kap-bygglov-rivningslov-marklov",
    title: "PBL 9 kap – Bygglov, rivningslov och marklov",
    h1: "PBL 9 kap – Bygglov, rivningslov och marklov",
    metaDescription:
      "PBL 9 kap reglerar bygglov, rivningslov och marklov. När krävs lov, när räcker anmälan och vilka undantag finns? Guide 2026.",
    keywords: "PBL 9 kap, bygglov, rivningslov, marklov, anmälan",
    intro:
      "Kapitel 9 är hjärtat i bygglovsprocessen – det räknar upp när lov krävs, vad som kan undantas (t.ex. attefall och friggebod) och vad kommunen ska pröva.",
    verified_source: { sfs: "2010:900", chapter: "9", paragraph: "2" },
    excerpt: {
      text: "Det krävs bygglov för nybyggnad, tillbyggnad och för andra ändringar av en byggnad än tillbyggnad, om ändringen innebär att byggnaden helt eller delvis tas i anspråk för ett väsentligen annat ändamål.",
      source: "PBL 9 kap. 2 § – bygglovspliktens omfattning",
    },
    examples: [
      { type: "Villa", title: "Tillbyggnad 20 m²", text: "Bygga till 20 m² kräver bygglov – Attefallsregeln (15 m²) räcker inte, alltså full bygglovsprövning." },
      { type: "Attefall", title: "Attefallshus 30 m²", text: "Attefallshus är undantaget från bygglovsplikt men kräver anmälan och startbesked enligt 9 kap. 4a–4c §§." },
      { type: "Rivning", title: "Riva garage", text: "Inom detaljplan krävs rivningslov för byggnad – utanför räcker rivningsanmälan." },
    ],
    mistakes: [
      "Att förväxla anmälan med bygglov – attefall kräver anmälan + startbesked innan bygget får påbörjas.",
      "Att starta byggarbete efter bygglov men innan startbesked – detta är sanktionsbelagt enligt PBL 11 kap.",
      "Att glömma marklov när tomt schaktas mer än 0,5 m inom detaljplanelagt område.",
    ],
    municipality_variations:
      "Vissa kommuner har utökad lovplikt via detaljplan (t.ex. färgsättning på fasad) – kolla alltid din specifika detaljplan.",
    faq: [
      { q: "När krävs bygglov?", a: "För nybyggnad, tillbyggnad, ändrad användning och betydande fasadändringar inom detaljplan. Utanför detaljplan är kraven mindre stränga." },
      { q: "Vad är skillnaden mellan bygglov och anmälan?", a: "Bygglov är en full prövning enligt PBL. Anmälan krävs för attefall, eldstad m.m. – utan planprövning men fortfarande med startbesked." },
      { q: "Vad händer om jag bygger utan lov?", a: "Byggnadsnämnden kan besluta om byggsanktionsavgift, föreläggande om rättelse eller rivning enligt PBL 11 kap." },
      { q: "Kan bygglov nekas trots att detaljplanen tillåter?", a: "Ja, om åtgärden strider mot annan lag (t.ex. Miljöbalken, strandskydd) eller om planen är inaktuell." },
      { q: "Hur lång är handläggningstiden?", a: "Enligt PBL 9 kap. 27 § max 10 veckor från komplett ansökan – kan förlängas 10 veckor vid komplexa ärenden." },
    ],
    howto: {
      name: "Så söker du bygglov enligt PBL 9 kap",
      description: "Steg-för-steg genom bygglovsansökan.",
      steps: [
        { name: "1. Kolla lovplikt", text: "Kontrollera om åtgärden kräver bygglov, anmälan eller är lovbefriad. Använd vår regelmotor." },
        { name: "2. Bedöm mot detaljplan", text: "Ligger tomten inom detaljplan? Läs planbestämmelser om byggnadsarea, höjder och användning." },
        { name: "3. Ta fram handlingar", text: "Situationsplan, planritning, fasadritning och sektionsritning i skala 1:100 (situationsplan 1:400)." },
        { name: "4. Skicka in ansökan", text: "Via kommunens e-tjänst eller pappersblankett. Bygglovskompassen genererar färdig PDF." },
        { name: "5. Invänta beslut", text: "Handläggningstid max 10 veckor. Kommunen kan begära kompletteringar." },
        { name: "6. Startbesked", text: "Bygglov + startbesked krävs innan byggarbetet får påbörjas." },
      ],
    },
    related: [
      "pbl/1-kap-syfte",
      "attefall/attefallshus-regler",
      "bygglov/altan",
      "bygglov/tillbyggnad",
    ],
    updated: "2026-07-04",
  },
  {
    slug: "5-kap-detaljplan",
    title: "PBL 5 kap – Detaljplan och områdesbestämmelser",
    h1: "PBL 5 kap – Så tas en detaljplan fram",
    metaDescription:
      "PBL 5 kap beskriver processen för att ta fram en detaljplan – från planbesked till antagande. Läs om samråd, granskning och överklagande.",
    keywords: "PBL 5 kap, detaljplan process, planbesked, samråd, granskning",
    intro:
      "Kapitel 5 reglerar planprocessen – hur kommunen tar fram en detaljplan från idé till antagande. Processen är öppen med samråd och granskning för allmänheten.",
    verified_source: { sfs: "2010:900", chapter: "5", paragraph: "11" },
    excerpt: {
      text: "Kommunen ska när en detaljplan tas fram bereda tillfälle till samråd med sakägare och boende som berörs.",
      source: "PBL 5 kap. 11 § – samrådskravet",
    },
    examples: [
      { type: "Villa", title: "Ny detaljplan", text: "Bor du inom eller intill planområdet får du samrådshandling i brevlådan – yttra dig innan granskningsskedet." },
    ],
    mistakes: [
      "Att missa samrådstiden (oftast 3 veckor) – yttrande sent i processen har mindre effekt.",
      "Att tro att bygglov kan sökas innan planen antagits – detta är bara möjligt via planbesked.",
    ],
    municipality_variations:
      "Utökat förfarande används i komplexa fall (t.ex. tätortsutveckling). Standardförfarande räcker för mindre planer.",
    faq: [
      { q: "Hur lång tid tar en detaljplan?", a: "Från planbesked till antagande normalt 1-3 år. Enkla planer kan gå snabbare." },
      { q: "Kan jag överklaga en detaljplan?", a: "Ja, som sakägare kan du överklaga antagandebeslutet till mark- och miljödomstolen inom 3 veckor." },
    ],
    related: ["pbl/9-kap-bygglov-rivningslov-marklov", "detaljplan/vad-ar-en-detaljplan"],
    updated: "2026-07-04",
  },
  {
    slug: "6-kap-tillsyn",
    title: "PBL 11 kap – Tillsyn och byggsanktionsavgift",
    h1: "PBL 11 kap – Tillsyn, ingripanden och byggsanktionsavgift",
    metaDescription:
      "PBL kap 11 om tillsyn: byggsanktionsavgift, föreläggande, rivningsföreläggande och vite. Så gör kommunens byggnadsnämnd vid olovligt bygge.",
    keywords: "PBL tillsyn, byggsanktionsavgift, olovligt bygge, föreläggande",
    intro:
      "PBL 11 kap ger kommunen verktyg att ingripa mot olovligt byggande – från byggsanktionsavgift till rivningsföreläggande. Preskriptionstid är 10 år.",
    verified_source: { sfs: "2010:900", chapter: "11", paragraph: "51" },
    excerpt: {
      text: "En byggsanktionsavgift ska tas ut om någon påbörjar en åtgärd som kräver bygglov, marklov, rivningslov eller anmälan innan byggnadsnämnden har gett startbesked.",
      source: "PBL 11 kap. 51 § – huvudregeln",
    },
    examples: [
      { type: "Villa", title: "Byggt utan lov", text: "Byggsanktionsavgift kan bli 25 % av prisbasbeloppet + 0,5 % av produktionskostnaden per m²." },
      { type: "Attefall", title: "Missad anmälan", text: "Även attefall utan startbesked leder till sanktion – trots att det är 'bygglovsbefriat'." },
    ],
    mistakes: [
      "Att tro att sanktionen försvinner om man söker lov i efterhand – avgiften kvarstår.",
      "Att bygga trots att grannar överklagat – kan leda till rivningskrav om beslutet ändras.",
    ],
    municipality_variations:
      "Byggnadsnämnden har viss möjlighet att sätta ned avgiften (max 50 %) om det finns särskilda skäl.",
    faq: [
      { q: "Hur stor blir byggsanktionsavgiften?", a: "Beräknas enligt PBF 9 kap – varierar med byggnadstyp och area. Ofta 20 000-200 000 kr för attefall/tillbyggnad." },
      { q: "Kan man överklaga byggsanktionsavgift?", a: "Ja, beslutet överklagas till länsstyrelsen inom 3 veckor." },
      { q: "Vad är preskriptionstiden?", a: "10 år från när åtgärden var slutförd. Efter det kan kommunen inte längre kräva rivning (men bygglov behövs fortfarande för framtida ändringar)." },
    ],
    related: ["pbl/9-kap-bygglov-rivningslov-marklov", "bygglov/rivningslov"],
    updated: "2026-07-04",
  },
];
